import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  Description as ReportIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

interface CommandAction {
  id: string;
  label: string;
  category: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
}

const CommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const actions: CommandAction[] = [
    { id: 'dash', label: 'Go to Dashboard', category: 'Navigation', icon: <DashboardIcon />, path: '/dashboard' },
    { id: 'users', label: 'Manage Students', category: 'Navigation', icon: <PeopleIcon />, path: '/users' },
    { id: 'challans', label: 'View Challans', category: 'Navigation', icon: <ReceiptIcon />, path: '/challans' },
    { id: 'courses', label: 'Manage Courses', category: 'Navigation', icon: <SchoolIcon />, path: '/courses' },
    { id: 'reports', label: 'System Reports', category: 'Navigation', icon: <ReportIcon />, path: '/reports' },
    { id: 'audit', label: 'Audit Logs', category: 'Navigation', icon: <HistoryIcon />, path: '/audit-logs' },
    { id: 'settings', label: 'System Settings', category: 'System', icon: <SettingsIcon />, path: '/settings' },
    { id: 'logout', label: 'Sign Out', category: 'System', icon: <LogoutIcon />, action: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }},
    { id: 'add-user', label: 'Quick Register Student', category: 'Actions', icon: <AddIcon />, path: '/users' },
  ];

  const filteredActions = actions.filter(action => 
    action.label.toLowerCase().includes(search.toLowerCase()) ||
    action.category.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSelect = (action: CommandAction) => {
    if (action.path) {
      navigate(action.path);
    } else if (action.action) {
      action.action();
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredActions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredActions.length) % filteredActions.length);
    } else if (e.key === 'Enter') {
      if (filteredActions[selectedIndex]) {
        handleSelect(filteredActions[selectedIndex]);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.6)',
              backdropFilter: 'blur(8px)',
              zIndex: 9999,
            }}
          />

          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            style={{
              position: 'fixed',
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '90%',
              maxWidth: '600px',
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 10000,
              overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.05)'
            }}
          >
            {/* Search Input */}
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <SearchIcon sx={{ color: '#94a3b8' }} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search commands, pages, and actions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  fontSize: '1.1rem',
                  color: '#1e293b',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                ESC
              </div>
            </div>

            {/* Results */}
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
              {filteredActions.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                  No results found for "{search}"
                </div>
              ) : (
                filteredActions.map((action, index) => (
                  <div
                    key={action.id}
                    onClick={() => handleSelect(action)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: selectedIndex === index ? '#f8fafc' : 'transparent',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ 
                        color: selectedIndex === index ? '#6366f1' : '#94a3b8',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {action.icon}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>{action.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{action.category}</div>
                      </div>
                    </div>
                    {selectedIndex === index && (
                      <div style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 600 }}>
                        Press Enter ↵
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
                <span style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '0 4px' }}>↑↓</span> to navigate
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
                <span style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '0 4px' }}>↵</span> to select
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
